
import React, { useState, useEffect, useRef } from "react";
import { FaCogs, FaLeaf, FaTruck, FaTachometerAlt, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion } from "framer-motion";
import styled from "styled-components";

// Data for Logistics Slider
const logisticsSlides = [
  {
    icon: <FaCogs size={40} />,
    title: "Intelligent Process Automation",
    subtitle: "Boost operational efficiency by automating workflows with AI and RPA.",
  },
  {
    icon: <FaLeaf size={40} />,
    title: "Responsible & Ethical AI",
    subtitle: "Deploy transparent, explainable, and bias-free machine learning models.",
  },
  {
    icon: <FaTruck size={40} />,
    title: "AI-Driven Product Innovation",
    subtitle: "Accelerate product development using generative AI and predictive modeling.",
  },
  {
    icon: <FaTachometerAlt size={40} />,
    title: "Real-Time Data Intelligence",
    subtitle: "Make smarter decisions with AI-powered dashboards and live analytics.",
  },
];

// Logistics Slider Component
export function LogisticsSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef(null);
  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((curr) => (curr + 1) % logisticsSlides.length);
    }, 3000);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div className="logistics-slider flex flex-col items-center w-full p-6 bg-black my-6">
      <h2 className="logistics-slider-title text-2xl font-bold mb-6 text-center text-white">
        AI & ML Innovations Driving Tech Excellence
      </h2>

      <div ref={sliderRef} className="logistics-slider-wrapper relative w-full max-w-4xl overflow-hidden">
        <div
          className="logistics-slider-track flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {logisticsSlides.map((slide, index) => (
            <motion.div
              key={index}
              className="logistics-slide min-w-full flex flex-col items-center justify-center shadow-lg rounded-lg p-6 mx-auto text-center bg-white text-black"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="logistics-slide-icon text-yellow-400 mb-4">{slide.icon}</div>
              <p className="logistics-slide-text text-xl font-semibold mb-2">{slide.title}</p>
              <p className="logistics-slide-subtitle text-sm text-gray-600">{slide.subtitle}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="logistics-slider-nav flex mt-4 space-x-2">
        {logisticsSlides.map((_, index) => (
          <button
            key={index}
            className={`logistics-slider-nav-button h-4 w-4 cursor-pointer rounded-full transition-colors duration-300 ${
              currentSlide === index ? "bg-yellow-400 scale-125" : "bg-gray-400"
            }`}
            onClick={() => {
              setCurrentSlide(index);
              resetTimer();
            }}
          ></button>
        ))}
      </div>
    </div>
  );
}

// Data for Services Slider - AI and ML Technology Focus
const servicesSlidesData = [
  { title: "Fintech, Banking, Financial Services & Insurance", description: "Our AI, Cloud, and Blockchain solutions drive innovation and efficiency in the financial sector." },
  { title: "Retail, E-Commerce & Distribution", description: "Empowering seamless operations and personalized experiences for modern retail and e-commerce businesses." },
  { title: "Non-Profit, Government, Education & Startups", description: "Providing scalable and secure tech foundations for impactful organizations and emerging ventures." },
  { title: "Travel, Logistics & Hospitality", description: "Optimizing operations and enhancing customer experiences with smart technology in travel and logistics." },
  { title: "Technology & Software", description: "Partnering with tech companies to build cutting-edge software and elevate their development capabilities." },
  { title: "Manufacturing & Industrial", description: "Implementing intelligent automation and data insights to transform manufacturing processes." },
];


// Services Slider Component
export function ServicesSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timerRef = useRef(null);

  const handleBtn = () => {
    console.log("Navigating to services page (if react-router-dom is configured)");
  }

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % servicesSlidesData.length);
    }, 3000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % servicesSlidesData.length);
    resetTimer();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + servicesSlidesData.length) % servicesSlidesData.length);
    resetTimer();
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <div className="services-slider flex flex-col items-center w-full p-6 my-6">
      <h2 className="services-slider-title text-2xl font-bold mb-4 text-center text-black">Industries we Serve</h2>
      <p className="services-slider-description text-gray-600 text-center mb-6 max-w-2xl">
        From intelligent process automation and secure data management to scalable cloud infrastructure, Deepbrik delivers custom solutions for maximum efficiency and innovation.
      </p>
      <button onClick={handleBtn} className="services-slider-button bg-black text-white font-semibold px-4 py-2 rounded-lg mb-6 hover:bg-yellow-400 cursor-pointer">
        Know More →
      </button>

      <div className="services-slider-container relative w-full max-w-5xl flex items-center justify-center">
        <button className="services-slider-arrow services-slider-arrow-left absolute left-2 top-1/2 transform -translate-y-1/2 bg-black text-white p-2 rounded-full shadow-lg cursor-pointer" onClick={prevSlide}>
          <FaArrowLeft />
        </button>

        <motion.div
          key={currentSlide}
          className="services-slide-content flex w-full justify-center"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="services-slide-card bg-yellow-400 text-black p-6 rounded-lg shadow-lg w-full max-w-sm text-center">
            <h3 className="services-slide-card-title text-lg font-semibold mb-2">{servicesSlidesData[currentSlide].title}</h3>
            <p className="services-slide-card-description text-sm">{servicesSlidesData[currentSlide].description}</p>
          </div>
        </motion.div>

        <button className="services-slider-arrow services-slider-arrow-right absolute right-2 top-1/2 transform -translate-y-1/2 bg-black text-white p-2 rounded-full shadow-lg cursor-pointer" onClick={nextSlide}>
          <FaArrowRight />
        </button>
      </div>
    </div>
  );
}


// Industries data
const industryData = [
  {
    title: "Fintech, Banking, Financial Services & Insurance",
    background:
      "url('https://images.unsplash.com/photo-1515165562835-cf5dfb3c57a0?auto=format&fit=crop&w=600&q=80')",
  },
  {
    title: "Retail, E-Commerce & Distribution",
    background:
      "url('https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80')",
  },
  {
    title: "Non-Profit, Government, Education & Startups",
    background:
      "url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80')",
  },
  {
    title: "Travel, Logistics & Hospitality",
    background:
      "url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=600&q=80')",
  },
  // {
  //   title: "Healthcare & Pharma",
  //   background:
  //     "url('https://images.unsplash.com/photo-1581093588401-4f54c379d99f?auto=format&fit=crop&w=600&q=80')",
  // },
  {
    title: "Technology & Software",
    background:
      "url('https://images.unsplash.com/photo-1607746882042-944635dfe10e')",
  },
  // {
  //   title: "Education & E-learning",
  //   background:
  //     "url('https://images.unsplash.com/photo-1581091012184-7a4e3c3c1b5b')",
  // },
  {
    title: "Manufacturing & Industrial",
    background:
      "url('https://images.unsplash.com/photo-1515377905703-c4788e51af15?auto=format&fit=crop&w=600&q=80')",
  },
];

const CarouselContainer = styled.div`
  max-width: 90%;
  margin: 40px auto;
  text-align: center;
  padding: 0 30px;
  
`;

const CarouselTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
  @media (min-width: 768px) {
    overflow-x: visible;
    scroll-snap-type: none;
    -webkit-overflow-scrolling: auto;
    &::-webkit-scrollbar {
      display: block;
    }
    scrollbar-width: auto;
    -ms-overflow-style: auto;
    justify-content: center;
  }
`;

const CarouselCard = styled.div`
  flex: 0 0 90%;
  margin: 0 20px;
  height: 200px;
  border-radius: 10px;
  color: #0e2147;
  font-weight: 700;
  display: flex;
  align-items: flex-end;
  padding: 15px;
  background-image: ${({ bg }) => bg};
  background-size: cover;
  background-position: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease-in-out;
  scroll-snap-align: start;

  @media (min-width: 768px) {
    flex: 0 0 23%;
    height: 300px;
    padding: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
  &:hover {
    transform: scale(1.05);
  }
`;

const NavButton = styled.button`
  background-color: white;
  color: black;
  border: none;
  cursor: pointer;
  font-size: 24px;
  border-radius: 40%;
  padding: 8px;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
  z-index: 1;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  @media (min-width: 768px) {
    font-size: 30px;
    padding: 10px;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
    position: relative;
    transform: translateY(0);
  }
`;

const PrevButton = styled(NavButton)`
  left: 10px;
  @media (min-width: 768px) {
    left: 0;
    margin-right: 10px;
  }
`;
const NextButton = styled(NavButton)`
  right: -30px;
  @media (min-width: 768px) {
    right: 0;
    margin-left: 5px;
  }
`;

const Dots = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: center;
`;

const Dot = styled.span`
  height: 8px;
  width: 8px;
  margin: 0 3px;
  background-color: ${({ active }) => (active ? "#717171" : "#bbb")};
  border-radius: 50%;
  display: inline-block;
  cursor: pointer;
  @media (min-width: 768px) {
    height: 10px;
    width: 10px;
    margin: 0 5px;
  }
`;

export default function Services() {
  const itemsPerPage = 4;
  const totalItems = industryData.length;
  const maxIndex = totalItems - itemsPerPage;
  const [index, setIndex] = useState(0);

  const nextSlide = () => {
    if (index < maxIndex) {
      setIndex(index + 1);
    }
  };

  const prevSlide = () => {
    if (index > 0) {
      setIndex(index - 1);
    }
  };

  const goToSlide = (i) => {
    if (i >= 0 && i <= maxIndex) {
      setIndex(i);
    }
  };

  const currentItems = industryData.slice(index, index + itemsPerPage);

  return (
    <>
      {/* Hero Section */}
      <header
  className="flex items-center justify-center p-6"
  style={{
    minHeight: "60vh",
    backgroundImage: 'url("/Images/imag19.jpeg")',
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  }}
>
  <div className="hero-text-container">
    <span className="hero-tag">Future-Ready Tech</span>
    <h1 className="hero-heading">
      Deepbrik – Build Smarter with AI, Cloud & Blockchain
    </h1>
    <p className="hero-subtext">
      Reimagine your infrastructure with seamless AI automation, secure blockchain systems, and dynamic cloud services — all in one place.
    </p>
    <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded transition duration-300 shadow-md hover:shadow-lg">
      Launch Your Project
    </button>
  </div>
</header>


      {/* Services Section */}
      <section className="services-section py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
         <h3 className="section-subtitle text-blue-600 text-center text-xl font-semibold uppercase tracking-wide mb-6 font-sans">
  Our Services
</h3>


          <h2 className="section-title text-3xl md:text-4xl font-bold text-center mb-8">We Provide the Best IT Solution Services</h2>
          <p className="section-desc text-gray-300 text-center text-lg mb-12 max-w-3xl mx-auto">
            At Deepbrik, we offer a full range of advanced technology services
            designed to empower businesses with scalable, intelligent,
            and secure solutions.
          </p>

          <div className="services-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">🧠</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                AI & Machine Learning Solutions
              </h3>

              <ul className="list-disc list-inside text-gray-300">
                <li>Custom ML Model Development</li>
                <li>Deep Learning for Vision & NLP</li>
                <li>Predictive Analytics & Forecasting</li>
                <li>Recommendation Systems</li>
              </ul>
            </div>
            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
        <div className="service-icon">🚚</div>
        <h3 className="text-xl font-semibold mb-2 text-white">
          Automobile & Fleet Intelligence
        </h3>
        <ul className="list-disc list-inside text-gray-300">
  <li>ADAS System Design & Sensor Fusion</li>
  <li>On-Board Diagnostics & Predictive Health Monitoring</li>
  <li>Driving Behavior Classification & Anomaly Detection</li>
  <li>Vehicle Trajectory Analysis & Route Optimization</li>
</ul>

      </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">📊</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Data Analytics & Business Intelligence
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>Data Cleaning, Transformation & Visualization</li>
                <li>Real-Time & Historical Data Dashboards</li>
                <li>KPI Tracking & Custom Reports</li>
                <li>Anomaly Detection & Root Cause Analysis</li>
              </ul>
            </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">💻</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Web Application Development
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>Full-Stack Web Application Development</li>
                <li>Responsive UI/UX with Modern Frameworks</li>
                <li>Backend API Integration & Database Design</li>
                <li>Cloud-native & Serverless Deployments</li>
              </ul>
            </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">🚦</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Smart Traffic & Mobility Intelligence
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>Traffic Flow Analysis & Prediction</li>
                <li>Vehicle Detection & Classification</li>
                <li>
                  Automated Violation Detection
                </li>
                <li>Smart Parking & Navigation Systems</li>
              </ul>
            </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">🖼️</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Document & Image Intelligence
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>Intelligent Document Processing (OCR + NLP)</li>
                <li>Medical Image Analysis</li>
                <li>Auto-tagging and Classification Systems</li>
                <li>AI-based Report Summarization</li>
              </ul>
            </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">💡</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                AI Consulting & Strategy
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>AI Feasibility Assessment</li>
                <li>End-to-End AI Roadmap Planning</li>
                <li>PoC/MVP Development</li>
                <li>Training & Workshops for Teams</li>
              </ul>
            </div>
            {/* <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">💡</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                AI Consulting & Strategy
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>AI Feasibility Assessment</li>
                <li>End-to-End AI Roadmap Planning</li>
                <li>PoC/MVP Development</li>
                <li>Training & Workshops for Teams</li>
              </ul>
            </div> */}

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">📧</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Newsletter Intelligence & Content Automation
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>AI-Driven Newsletter Creation & Scheduling</li>
                <li>Content Summarization & Personalization</li>
                <li>Engagement & Open-rate Optimization</li>
                <li>Integration with Mailchimp, Substack, etc.</li>
              </ul>
            </div>

            <div className="service-card bg-gray-800 rounded-lg shadow-lg p-6 transition-transform duration-300 hover:scale-105">
              <div className="service-icon">☁️</div>
              <h3 className="text-xl font-semibold mb-2 text-white">
                Cloud & DevOps for AI
              </h3>
              <ul className="list-disc list-inside text-gray-300">
                <li>MLOps & Continuous Deployment</li>
                <li>Docker/Kubernetes Setup</li>
                <li>Scalable Cloud Architecture</li>
                <li>CI/CD Pipelines for AI/ML Workflows</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      {/* Logistics Slider */}
      <LogisticsSlider />
      
      {/* Process Section */}
      <section className="process-section py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <h3 className="section-subtitle text-blue-400 text-center text-xl md:text-2xl uppercase tracking-widest mb-10">
            How We Build Your Success
          </h3>
          <h2 className="section-title text-3xl md:text-4xl font-bold text-center mb-10">
            Our Development Process
          </h2>
          <div className="process-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Requirements Gathering",
                desc: "We engage with stakeholders to gather comprehensive project requirements and understand their goals through collaborative discussions.",
                icon: "https://img.icons8.com/ios/100/task.png",
                step: "01",
              },
              {
                title: "Design & Planning",
                desc: "Our team crafts a user-centric design and creates a detailed project plan, outlining the architecture, timelines, and deliverables.",
                icon: "https://img.icons8.com/ios/100/blueprint.png",
                step: "02",
              },
              
              {
                title: "Development",
                desc: "Our skilled developers bring your vision to life, leveraging modern technologies to build a secure and scalable product that meets all specifications.",
                icon: "https://img.icons8.com/ios/100/code--v1.png",
                step: "03",
              },
              {
                title: "Testing",
                desc: "We subject your product to rigorous testing to ensure flawless performance across all devices, identifying and resolving any issues before launch.",
                icon: "https://img.icons8.com/ios/100/inspection.png",
                step: "04",
              },
              {
                title: "Support & Maintenance",
                desc: "Our commitment extends beyond launch, providing ongoing maintenance and support to keep your product thriving in the ever-evolving digital landscape.",
                icon: "https://img.icons8.com/ios/100/handshake.png",
                step: "05",
              },
              {
                title: "Deployment",
                desc: "We ensure a smooth and efficient deployment process, making your product live with all configurations optimized for performance, security, and scalability.",
                icon: "https://img.icons8.com/ios/100/rocket--v1.png",
                step: "06",
              },
              
            ].map((item, i) => (
              <div
                key={i}
                className="process-card bg-gray-800 rounded-lg shadow-md p-6 transition-transform duration-300 hover:scale-105 flex flex-col items-center text-center"
              >
                <span className="step-number text-blue-400 font-bold text-2xl mb-4">
                  {item.step}
                </span>
                <img
                  src={item.icon}
                  alt={item.title}
                  className="process-icon mb-4 h-16"
                />
                <h4 className="text-xl font-semibold mb-2 text-white">
                  {item.title}
                </h4>
                <p className="text-gray-400 mb-4">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
{/* Services Slider */}
      <ServicesSlider />
  
<section class="insights-section">
  <h2>Insights and Innovations</h2>
  <div class="insights-cards">
    <div class="insight-card">
      <img src="https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=600&q=80" alt="Machine Learning" />
      <h3>How Custom Machine Learning Solutions Can Transform Your...</h3>
      <p>
        AI and Machine Learning (ML) are no longer just buzzwords—they’re reshaping industries. They analyze
        massive amounts of data, automate repetitive tasks, and uncover…
      </p>
      <a href="/services#machine-learning" class="read-more-btn">Read More</a>
    </div>

    <div class="insight-card">
      <img src="https://images.unsplash.com/photo-1593642632823-8f785ba67e45?auto=format&fit=crop&w=600&q=80" alt="Web Design" />
      <h3>How to Create a Responsive Website for Small Businesses in 2024</h3>
      <p>
        People use all kinds of devices—phones, tablets, and computers—to browse the internet. That’s why your
        small business needs a website that works smoothly on all screens. …
      </p>
      <a href="/services#web-design" class="read-more-btn">Read More</a>
    </div>

    <div class="insight-card">
      <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=600&q=80" alt="Cybersecurity" />
      <h3>Why Data Security is Non-Negotiable in Healthcare: Key Tips…</h3>
      <p>
        Let’s talk about something that’s becoming increasingly urgent: data security in healthcare. Did you
        know that in 2023, the average cost of a healthcare data breach was …
      </p>
      <a href="/services#cybersecurity" class="read-more-btn">Read More</a>
    </div>
  </div>
</section>
      <section className="contact-section">
        <div className="contact-container">
          <h2>Contact Us</h2>
          <form className="contact-form">
            <input type="text" placeholder="Your Name" required />
            <input type="email" placeholder="Your Email" required />
            <textarea placeholder="Your Message" required></textarea>
            <button type="submit">SEND MESSAGE</button>
          </form>
        </div>
      </section>

      
    </>
  );
}

const styles = `
  /* General Styles for both sliders */
  .flex { display: flex; }
  .flex-col { flex-direction: column; }
  .items-center { align-items: center; }
  .w-full { width: 100%; }
  .p-6 { padding: 1.5rem; }
  .my-6 { margin-top: 1.5rem; margin-bottom: 1.5rem; }
  .text-2xl { font-size: 1.5rem; }
  .font-bold { font-weight: 700; }
  .mb-6 { margin-bottom: 1.5rem; }
  .text-center { text-align: center; }
  .text-white { color: #fff; }
  .relative { position: relative; }
  .max-w-4xl { max-width: 56rem; }
  .overflow-hidden { overflow: hidden; }
  .transition-transform { transition-property: transform; }
  .duration-500 { transition-duration: 500ms; }
  .ease-in-out { transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1); }
  .min-w-full { min-width: 100%; }
  .justify-center { justify-content: center; }
  .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); }
  .rounded-lg { border-radius: 0.5rem; }
  .mx-auto { margin-left: auto; margin-right: auto; }
  .bg-white { background-color: #fff; }
  .text-black { color: #000; }
  .text-yellow-400 { color: black; }
  .mb-4 { margin-bottom: 1rem; }
  .text-lg { font-size: 1.125rem; }
  .font-medium { font-weight: 500; }
  .mt-4 { margin-top: 1rem; }
  .space-x-2 > :not([hidden]) ~ :not([hidden]) { margin-right: calc(0.5rem * var(--tw-space-x-reverse)); margin-left: calc(0.5rem * (1 - var(--tw-space-x-reverse))); }
  .h-4 { height: 1rem; }
  .w-4 { width: 1rem; }
  .cursor-pointer { cursor: pointer; }
  .rounded-full { border-radius: 9999px; }
  .transition-colors { transition-property: background-color, border-color, color, fill, stroke; }
  .duration-300 { transition-duration: 300ms; }
  .scale-125 { transform: scale(1.25); }
  .bg-gray-400 { background-color: #9ca3af; }

  /* Logistics Slider Specific Styles */
  .logistics-slider {
    background-color: #0e2147;
    padding: 40px 20px;
    margin: 40px auto;
    border-radius: 12px;
    max-width: 800px; /* Adjusted for better centering on larger screens */
    text-align: center;
    box-sizing: border-box; /* Ensures padding doesn't add to width */
  }

  .logistics-slider-title {
    font-size: 28px;
    font-weight: bold;
    color: white;
    margin-bottom: 30px;
  }

  .logistics-slider-wrapper {
    overflow: hidden;
    max-width: 600px; /* Adjusted for slide content width */
    margin: 0 auto;
    border-radius: 12px;
  }

  .logistics-slider-track {
    display: flex;
    transition: transform 0.5s ease-in-out;
  }

  .logistics-slide {
    min-width: 100%; /* Ensures only one slide shows at a time */
    padding: 24px;
    background-color: white;
    border-radius: 10px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
  }

  .logistics-slide-icon {
    font-size: 40px;
    color: #0e2147; /* Changed to match theme */
    margin-bottom: 16px;
  }

  .logistics-slide-text {
    font-size: 18px;
    font-weight: 500;
    color: #111827;
  }

  .logistics-slide-subtitle { /* Added subtitle style */
    font-size: 0.875rem; /* text-sm */
    color: black; /* text-gray-600 */
    margin-top: 0.5rem;
  }

  .logistics-slider-nav {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
  }

  .logistics-slider-nav-button {
    width: 14px;
    height: 14px;
    background-color: #ccc;
    border-radius: 40%;
    cursor: pointer;
    transition: background-color 0.3s;
    border: none;
  }

  .logistics-slider-nav-button.bg-yellow-400 { /* Tailwind class for active state */
    background-color: white;
  }
  .logistics-slider-nav-button.bg-gray-400 { /* Tailwind class for inactive state */
    background-color: black;
  }


  /* Slider wrapper (White container) */
.services-slider {
  padding: 16px 0; /* Reduced vertical padding */
  margin: 16px 0;  /* Reduced outer spacing */
  background-color: #f8f8f8;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

/* Title and description */
.services-slider-title {
  font-size: 30px;
  font-weight: bold;
  color: black;
  text-align: center;
  margin-bottom: 12px;
}

.services-slider-description {
  font-size: 18px;
  line-height: 1.5;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;
}

/* CTA Button */
.services-slider-button {
  background-color: #0e2147;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  text-align: center;
  display: block;
  margin: 0 auto 24px;
  width: fit-content;
  transition: background-color 0.3s ease;
}
.services-slider-button:hover {
  background-color: #0e2147;
}

/* Slider container (Holds arrows + card) */
.services-slider-container {
  position: relative;
  width: 100%;
  padding: 0 24px; /* Reduced side padding */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  background-color:white;
  justify-content: center;
}

/* Arrow buttons */
.services-slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: #0e2147;
  color: white;
  padding: 6px;
  font-size: 14px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s ease;
}
.services-slider-arrow:hover {
  background-color:  #0e2147;
}
.services-slider-arrow-left {
  left: 8px;
}
.services-slider-arrow-right {
  right: 8px;
}

/* Card wrapper */
.services-slide-content {
  flex-grow: 1;
  display: flex;
  justify-content: center;
}

/* Service card (Yellow box) */
.services-slide-card {
  background-color:  #0e2147;
  color: white;
  padding: 24px;
  border-radius: 10px;
  width: 80%;
  max-width: 384px;
  text-align: center;
  box-sizing: border-box;
}

/* Card text */
.services-slide-card-title {
  font-size: 18px;
  font-weight: 600;
  color:white;
  margin-bottom: 8px;
}
.services-slide-card-description {
  font-size: 14px;
  color: white;
}

/* Responsive Fix: Tablets */
@media (max-width: 768px) {
  .services-slider {
    padding: 12px 0;
    margin: 12px 0;
  }

  .services-slider-container {
    padding: 0 20px;
    flex-direction: column;
  }

  .services-slide-card {
    width: 100%;
    max-width: 100%;
    padding: 16px;
    margin: 0 auto;
  }

  .services-slider-arrow {
    padding: 5px;
    font-size: 13px;
  }

  .services-slider-arrow-left {
    left: 6px;
  }

  .services-slider-arrow-right {
    right: 6px;
  }

  .services-slider-title {
    font-size: 20px;
    margin-bottom: 10px;
  }

  .services-slider-description {
    font-size: 14px;
    margin-bottom: 16px;
    padding: 0 10px;
  }

  .services-slider-button {
    padding: 8px 16px;
    font-size: 14px;
    margin-bottom: 20px;
  }

  .services-slide-card-title {
    font-size: 16px;
  }

  .services-slide-card-description {
    font-size: 13px;
  }
}

/* Responsive Fix: Phones */
@media (max-width: 480px) {
  .services-slider {
    padding: 12px 0;
    margin: 10px 0;
  }

  .services-slider-container {
    padding: 0 16px;
  }

  .services-slide-card {
    padding: 12px;
    width: 100%;
    max-width: 100%;
  }

  .services-slider-arrow {
    padding: 4px;
    font-size: 12px;
  }

  .services-slider-arrow-left {
    left: 4px;
  }

  .services-slider-arrow-right {
    right: 4px;
  }

  .services-slider-title {
    font-size: 18px;
  }

  .services-slider-description {
    font-size: 13px;
  }

  .services-slider-button {
    font-size: 13px;
    padding: 8px 14px;
  }
}




  /* Resources Section Styles */
  .resources-section {
    padding: 80px 0;
    text-align: center;
    background-color: #f0f0f0; /* Added a light background */
  }
  
  .resources-section h2 {
    font-size: 36px;
    margin-bottom: 40px;
    font-weight: bold;
    color: #0e2147;
  }
  
  .resources-section h2 span {
    color: #007bff;
  }
  
  .resources-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
  
  .resource-card {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: transform 0.3s ease;
  }
  
  .resource-card:hover {
    transform: translateY(-10px);
  }
  
  .resource-card img {
    width: 100%;
    height: 250px;
    object-fit: cover;
  }
  
  .resource-content {
    padding: 20px;
    text-align: left;
  }
  
  .resource-date {
    font-size: 14px;
    color: #6c757d;
    margin-bottom: 10px;
  }
  
  .resource-title {
    font-size: 20px;
    font-weight: bold;
    color: #0e2147;
    margin-bottom: 10px;
  }
  
  .resource-description {
    font-size: 16px;
    color: #343a40;
    line-height: 1.6;
  }
  
  @media (min-width: 768px) {
    .resources-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  
  @media (min-width: 992px) {
    .resources-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

